import React, { useState } from 'react';
import { ExportDestination, ExportFlowAction } from '../types';
import { isHttpsUrl } from '../utils/exportFlow';

interface ExportDestinationListProps {
    destinations: ExportDestination[];
    /** Absolute url van het icoon op de backend, of null */
    resolveIconUrl: (iconUrl: string) => string | null;
    /** ready: de wizard; web_only of actie web: meteen de webstap */
    onStart: (destination: ExportDestination, web: boolean) => void;
    onOpenUrl: (url: string) => void;
}

const DestinationIcon: React.FC<{ url: string | null; muted?: boolean }> = ({ url, muted }) => {
    const [failed, setFailed] = useState(false);
    if (url && !failed) {
        return <img src={url} alt="" className={`w-5 h-5 ${muted ? 'opacity-60' : ''}`} onError={() => setFailed(true)} />;
    }
    return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
    );
};

/**
 * De bestemmingen uit GET /export/v2/…/destinations, elk met naam, platform en
 * toestand. Teksten van de server staan er als platte tekst.
 */
const ExportDestinationList: React.FC<ExportDestinationListProps> = ({ destinations, resolveIconUrl, onStart, onOpenUrl }) => (
    <ul className="space-y-3">
        {destinations.map((destination) => {
            const { state, platform } = destination;
            const iconUrl = platform.icon_url ? resolveIconUrl(platform.icon_url) : null;
            const subtitle = platform.label && platform.label !== destination.name ? platform.label : null;

            if (state.kind === 'ready' || state.kind === 'web_only') {
                const web = state.kind === 'web_only';
                return (
                    <li key={destination.config_id}>
                        <button
                            type="button"
                            onClick={() => onStart(destination, web)}
                            className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                        >
                            <span className="flex-shrink-0 text-gray-600 mr-3"><DestinationIcon url={iconUrl} /></span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-medium text-gray-900 text-sm break-words">{destination.name}</span>
                                {web && state.message
                                    ? <span className="block text-gray-500 text-xs whitespace-pre-line">{state.message}</span>
                                    : subtitle && <span className="block text-gray-500 text-xs">{subtitle}</span>}
                            </span>
                        </button>
                    </li>
                );
            }

            // needs_link, needs_reconnect, unavailable (of een toestand die de webportal niet kent)
            const action: ExportFlowAction | undefined = state.action;
            const showAction = action && ((action.type === 'open_url' && isHttpsUrl(action.url)) || action.type === 'web');
            return (
                <li key={destination.config_id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center">
                        <span className="flex-shrink-0 text-gray-400 mr-3"><DestinationIcon url={iconUrl} muted /></span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-medium text-gray-700 text-sm break-words">{destination.name}</span>
                            {subtitle && <span className="block text-gray-500 text-xs">{subtitle}</span>}
                        </span>
                    </div>
                    {state.message && <p className="mt-2 text-xs text-gray-600 whitespace-pre-line break-words">{state.message}</p>}
                    {showAction && action && (
                        <button
                            type="button"
                            onClick={() => (action.type === 'web' ? onStart(destination, true) : onOpenUrl(action.url!))}
                            className="mt-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {action.label}
                        </button>
                    )}
                </li>
            );
        })}
    </ul>
);

export default ExportDestinationList;
